import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, Loader2, AlertTriangle } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/sign`;
const detail = (e) => e?.response?.data?.detail || "Noe gikk galt. Prøv igjen.";

const Box = ({ children }) => (
  <div className="min-h-screen bg-zinc-100 px-4 py-8 text-zinc-900" data-testid="signer-page">
    <div className="mx-auto max-w-xl rounded-sm border-t-4 border-[#c8102e] bg-white p-6 shadow-sm sm:p-8">{children}</div>
  </div>
);

export default function Signer() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [read, setRead] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    axios.get(`${API}/${token}`).then((r) => { setInfo(r.data); setName(r.data?.buyer_name ?? ""); }).catch((e) => setErr(detail(e)));
    return () => { document.head.removeChild(m); };
  }, [token]);

  const openPdf = async () => {
    const w = window.open("", "_blank");
    try {
      const r = await axios.get(`${API}/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); setErr(detail(e)); }
  };
  const submit = async () => {
    setBusy(true);
    try { await axios.post(`${API}/${token}`, { name, image: sig, accepted: read }); setDone(true); } catch (e) { setErr(detail(e)); } finally { setBusy(false); }
  };

  if (done) return <Box><CheckCircle2 className="text-emerald-600" size={40} /><h1 className="mt-4 text-2xl font-bold" data-testid="signer-done">Takk – dokumentet er signert</h1><p className="mt-2 text-sm text-zinc-600">Du får en kopi av det ferdig signerte dokumentet fra {info?.seller}. Du kan lukke denne siden.</p></Box>;
  if (err && !info) return <Box><AlertTriangle className="text-amber-600" size={36} /><h1 className="mt-4 text-xl font-bold">Lenken kan ikke brukes</h1><p className="mt-2 text-sm text-zinc-600" data-testid="signer-error">{err}</p></Box>;
  if (!info) return <Box><Loader2 className="animate-spin text-zinc-400" /></Box>;
  return (
    <Box>
      <p className="text-xs font-bold uppercase tracking-widest text-[#c8102e]">{info.seller} · Elektronisk signering</p>
      <h1 className="mt-2 text-2xl font-bold" data-testid="signer-title">{info.title} {info.number}</h1>
      <p className="mt-1 text-sm text-zinc-600">{info.vehicle}</p>
      <button type="button" onClick={openPdf} data-testid="signer-open-pdf" className="mt-5 flex w-full items-center justify-center gap-2 border border-zinc-300 py-3 text-sm font-semibold hover:border-zinc-500">
        <FileText size={16} /> Åpne og les hele dokumentet (PDF)
      </button>
      <label className="mt-5 block text-xs font-semibold uppercase tracking-wider text-zinc-500" htmlFor="signer-name">Fullt navn</label>
      <input id="signer-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signer-name" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:border-[#c8102e]" />
      <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-zinc-500">Signatur</p>
      <div className="mt-1 border border-zinc-300"><SignaturePad onChange={setSig} testid="signer-pad" /></div>
      <label className="mt-5 flex items-start gap-2 text-sm">
        <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="mt-1" data-testid="signer-accept" />
        Jeg har lest hele dokumentet, inkludert opplysninger om bilens tilstand og vilkår, og signerer det elektronisk.
      </label>
      {err && <p className="mt-3 text-sm text-red-600" data-testid="signer-submit-error">{err}</p>}
      <button type="button" onClick={submit} disabled={!sig || !read || name.trim().length < 2 || busy} data-testid="signer-submit"
        className="mt-6 flex w-full items-center justify-center gap-2 bg-[#c8102e] py-3.5 font-bold uppercase tracking-widest text-white disabled:opacity-40">
        {busy && <Loader2 size={16} className="animate-spin" />} Signer dokumentet
      </button>
      <p className="mt-4 text-xs text-zinc-500">Tidspunkt og IP-adresse lagres sammen med signaturen. Lenken kan bare brukes én gang.</p>
    </Box>
  );
}
